// View derivations for the engine-backed production-deviation workflow.
import {
  ProductionDeviationActionItem,
  ProductionDeviationActionOutcome,
  ProductionDeviationAssessment,
  ProductionDeviationCase,
  ProductionDeviationOutcome,
  ProductionDeviationRole,
  ProductionDeviationStep,
  ProductionDeviationVerdict,
  WorkflowItem,
} from '#models';
import type { Uc2StageStatus } from '../models/uc2-view-state';

export const PRODUCTION_DEVIATION_STEP_LABELS: Record<ProductionDeviationStep, string> = {
  [ProductionDeviationStep.ConfirmFindings]: 'Confirm findings',
  [ProductionDeviationStep.ApproveFindings]: 'Approve findings',
  [ProductionDeviationStep.ExecuteActions]: 'Execute actions',
  [ProductionDeviationStep.Closed]: 'Findings recorded',
};

export const PRODUCTION_DEVIATION_ROLE_LABELS: Record<ProductionDeviationRole, string> = {
  [ProductionDeviationRole.IoAnalyst]: 'IO-Analyst',
  [ProductionDeviationRole.IoProductionEngineer]: 'IO-Production-Engineer',
  [ProductionDeviationRole.IoAdmin]: 'IO-Admin',
  [ProductionDeviationRole.IoFieldSpecialist]: 'IO-Field-Specialist',
  [ProductionDeviationRole.IoUser]: 'IO-User',
};

export const PRODUCTION_DEVIATION_ASSESSMENT_LABELS: Record<ProductionDeviationAssessment, string> = {
  [ProductionDeviationAssessment.MeasurementIssue]: 'Measurement issue',
  [ProductionDeviationAssessment.AllocationInputIssue]: 'Allocation input issue',
  [ProductionDeviationAssessment.RoutingConstraint]: 'Routing constraint',
  [ProductionDeviationAssessment.Undetermined]: 'Undetermined',
};

export const PRODUCTION_DEVIATION_OUTCOME_LABELS: Record<ProductionDeviationOutcome, string> = {
  dismissed: 'Dismissed as not actionable',
  all_done: 'All actions done',
  incomplete: 'Actions incomplete',
};

/**
 * The defect disposition vocabulary has no agreed values yet (OPEN-U1-02), so approval records its placeholder
 * without asking the user.
 */
export const PRODUCTION_DEVIATION_DEFECT_DISPOSITION = 'TBD';

/** UC1 roles from most to least privileged: Approver, Originator, Executor. */
const DEFECT_OWNER_PRIVILEGE: readonly ProductionDeviationRole[] = [
  ProductionDeviationRole.IoProductionEngineer,
  ProductionDeviationRole.IoAnalyst,
  ProductionDeviationRole.IoFieldSpecialist,
];

/** The approver's most privileged UC1 role, recorded as the defect owner; null when they hold none. */
export function defectOwnerFor(roles: readonly ProductionDeviationRole[]): ProductionDeviationRole | null {
  return DEFECT_OWNER_PRIVILEGE.find(role => roles.includes(role)) ?? null;
}

export type ProductionDeviationActionStatus =
  | ProductionDeviationActionOutcome
  | 'recorded'
  | 'active'
  | 'pending'
  | 'planned'
  | 'excluded'
  | 'awaiting-selection';

export const PRODUCTION_DEVIATION_ACTION_STATUS_LABELS: Record<ProductionDeviationActionStatus, string> = {
  done: 'Done',
  not_applicable: 'Not applicable',
  cannot_complete: 'Cannot complete',
  recorded: 'Outcome unavailable',
  active: 'In progress',
  pending: 'Pending',
  planned: 'Planned',
  excluded: 'Not selected',
  'awaiting-selection': 'Awaiting selection',
};

export interface ProductionDeviationStageView {
  label: string;
  status: Uc2StageStatus;
  detail: string;
}

export interface ProductionDeviationActionRow {
  item: ProductionDeviationActionItem;
  candidateLabel: string;
  status: ProductionDeviationActionStatus;
}

const STEP_ORDER = [
  ProductionDeviationStep.ConfirmFindings,
  ProductionDeviationStep.ApproveFindings,
  ProductionDeviationStep.ExecuteActions,
  ProductionDeviationStep.Closed,
];

export function isProductionDeviationItem(item: Pick<WorkflowItem, 'identity'> | null | undefined): boolean {
  const identity = item?.identity;
  if (!identity) {
    return false;
  }
  return /\buc1\b/i.test(identity.useCase ?? '') || /export volume deviation/i.test(identity.exceptionType ?? '');
}

export function resolvedItems(workflowCase: ProductionDeviationCase): ProductionDeviationActionItem[] {
  const resolved = workflowCase.resolvedItemIds;
  if (!resolved) {
    return [];
  }
  return resolved
    .map(id => workflowCase.actionItems.find(item => item.id === id))
    .filter((item): item is ProductionDeviationActionItem => !!item);
}

export function activeActionItem(workflowCase: ProductionDeviationCase): ProductionDeviationActionItem | null {
  if (workflowCase.step !== ProductionDeviationStep.ExecuteActions) {
    return null;
  }
  return resolvedItems(workflowCase).find(item => !workflowCase.completions[item.id]) ?? null;
}

/** Concrete role whose queue the case currently sits in; null once closed. */
export function stepRole(workflowCase: ProductionDeviationCase): ProductionDeviationRole | null {
  switch (workflowCase.step) {
    case ProductionDeviationStep.ConfirmFindings:
      return ProductionDeviationRole.IoAnalyst;
    case ProductionDeviationStep.ApproveFindings:
      return ProductionDeviationRole.IoProductionEngineer;
    case ProductionDeviationStep.ExecuteActions:
      return activeActionItem(workflowCase)?.targetRole ?? null;
    default:
      return null;
  }
}

export function queueSummary(workflowCase: ProductionDeviationCase): string {
  if (workflowCase.step === ProductionDeviationStep.Closed) {
    return 'Closed';
  }
  if (workflowCase.owner) {
    return `Owned by ${workflowCase.owner}`;
  }
  const role = stepRole(workflowCase);
  return role ? `Waiting in ${PRODUCTION_DEVIATION_ROLE_LABELS[role]} queue` : 'Unowned';
}

export function detectionLatencyLabel(workflowCase: Pick<ProductionDeviationCase, 'dataForDate' | 'detectedUtc'>): string {
  const dayStart = Date.parse(workflowCase.dataForDate.slice(0, 10));
  const detected = Date.parse(workflowCase.detectedUtc);
  if (Number.isNaN(dayStart) || Number.isNaN(detected)) {
    return '--';
  }
  const dayClose = dayStart + 24 * 3_600_000;
  const hours = Math.max(0, (detected - dayClose) / 3_600_000);
  return `${hours.toFixed(1)} h after production day close`;
}

export function buildActionRows(workflowCase: ProductionDeviationCase): ProductionDeviationActionRow[] {
  const { findings, resolvedItemIds, completions, candidates } = workflowCase;
  const active = activeActionItem(workflowCase);

  return workflowCase.actionItems.map(item => {
    const candidate = candidates.find(entry => entry.candidateId === item.candidateId);
    const candidateLabel = item.candidateId === null ? 'Case-wide' : (candidate?.title ?? item.candidateId);
    const selected = item.candidateId === null || findings.selectedCandidateIds.includes(item.candidateId);

    let status: ProductionDeviationActionStatus;
    if (resolvedItemIds) {
      if (!resolvedItemIds.includes(item.id)) {
        status = 'excluded';
      } else if (completions[item.id]) {
        status = completions[item.id].outcome ?? 'recorded';
      } else {
        status = active?.id === item.id ? 'active' : 'pending';
      }
    } else if (findings.verdict === ProductionDeviationVerdict.Confirmed) {
      status = selected ? 'planned' : 'excluded';
    } else {
      status = 'awaiting-selection';
    }
    return { item, candidateLabel, status };
  });
}

export function buildStages(workflowCase: ProductionDeviationCase): ProductionDeviationStageView[] {
  const { step, outcome, findings, owner } = workflowCase;
  const currentIndex = STEP_ORDER.indexOf(step);
  const dismissed = outcome === 'dismissed';
  const items = resolvedItems(workflowCase);
  const skippedExecute = workflowCase.resolvedItemIds?.length === 0;

  return STEP_ORDER.map((entry, index) => {
    const label = PRODUCTION_DEVIATION_STEP_LABELS[entry];

    let status: Uc2StageStatus;
    if (dismissed && index > 0 && index < STEP_ORDER.length - 1) {
      status = 'skipped';
    } else if (entry === ProductionDeviationStep.ExecuteActions && skippedExecute && index < currentIndex) {
      status = 'skipped';
    } else if (index < currentIndex || step === ProductionDeviationStep.Closed) {
      status = 'done';
    } else if (index === currentIndex) {
      status = 'current';
    } else {
      status = 'pending';
    }

    let detail: string;
    switch (entry) {
      case ProductionDeviationStep.ConfirmFindings:
        detail = status === 'done'
          ? (dismissed ? 'Dismissed' : `Confirmed · ${PRODUCTION_DEVIATION_ASSESSMENT_LABELS[findings.assessment ?? ProductionDeviationAssessment.Undetermined]}`)
          : status === 'current' ? (owner ? `Owned by ${owner}` : 'In IO-Analyst queue') : 'IO-Analyst';
        break;
      case ProductionDeviationStep.ApproveFindings:
        detail = status === 'done'
          ? 'Approved'
          : status === 'current' ? (owner ? `Owned by ${owner}` : 'In IO-Production-Engineer queue') : 'IO-Production-Engineer';
        break;
      case ProductionDeviationStep.ExecuteActions:
        detail = status === 'skipped' && !dismissed
          ? 'No actions selected'
          : items.length ? actionProgressSummary(workflowCase) : 'Per action item';
        break;
      default:
        detail = outcome ? PRODUCTION_DEVIATION_OUTCOME_LABELS[outcome] : 'Typed outcome required';
    }
    return { label, status, detail: dismissed && status === 'skipped' ? 'Not reached' : detail };
  });
}

export function actionProgressSummary(workflowCase: ProductionDeviationCase): string {
  const items = resolvedItems(workflowCase);
  const recorded = items.flatMap(item => workflowCase.completions[item.id] ? [workflowCase.completions[item.id]] : []);
  const count = (outcome: ProductionDeviationActionOutcome | null) => recorded.filter(entry => entry.outcome === outcome).length;
  const parts = [`${recorded.length} of ${items.length} resolved`, `${count('done')} done`];
  for (const outcome of ['not_applicable', 'cannot_complete'] as const) {
    if (count(outcome)) {
      parts.push(`${count(outcome)} ${PRODUCTION_DEVIATION_ACTION_STATUS_LABELS[outcome].toLowerCase()}`);
    }
  }
  if (count(null)) {
    parts.push(`${count(null)} outcome unavailable`);
  }
  return parts.join(' · ');
}
